<template>
  <div style="position: relative">
    <v-card flat>
      <div id="controlsContainer">
        <div
          class="card-title table-controls px-3 d-flex align-center justify-space-between"
        >
          <div>
            <!-- CRUD buttons -->
            <!-- dialog for creating item -->
            <crud-button
              v-if="$checkPermission('CLIENTS')"
              x-large
              color="light-blue lighten-2"
              @clicked="create()"
              icon="fa-plus"
              :tooltip="$t('global.crud.button.add')"
            />
          </div>
          <!-- center side -->
          <div>
            <!-- Search by fields -->
            <!-- Search in table -->
            <v-container id="filterContainer">
              <v-row>
                <v-col lg="3" sm="12" cols="12">
                  <v-text-field
                    v-model="filter.allFields"
                    :label="$t('global.datatable.search')"
                    append-icon="fas fa-search"
                    single-line
                  ></v-text-field>
                </v-col>
                <v-col lg="2" sm="6" cols="12">
                  <v-select
                    v-model="filter.deletedItems"
                    :items="deletedItemsValues"
                    :label="$t('global.status')"
                  ></v-select>
                </v-col>
                <v-col lg="2" sm="6" cols="12">
                  <v-select
                    v-model="filter.autoscheduling"
                    :items="autoschedulingValues"
                    :label="$t('clients.autoscheduling')"
                  ></v-select>
                </v-col>
                <v-col lg="2" sm="6" cols="12">
                  <v-dialog
                    ref="dialogFilterAutoschedulingCutOffTimeFrom"
                    v-model="modalFilterAutoschedulingCutOffTimeFrom"
                    :return-value.sync="
                      filter.autoschedulingCutOffTimeFromString
                    "
                    persistent
                    width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filter.autoschedulingCutOffTimeFromString"
                        :label="$t('clients.autoschedulingCutOffTimeFrom')"
                        readonly
                        clearable
                        append-icon="far fa-clock"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker
                      v-if="modalFilterAutoschedulingCutOffTimeFrom"
                      v-model="filter.autoschedulingCutOffTimeFromString"
                      full-width
                      format="24hr"
                      :allowed-minutes="allowedStepTime"
                      @click:minute="
                        $refs.dialogFilterAutoschedulingCutOffTimeFrom.save(
                          filter.autoschedulingCutOffTimeFromString
                        )
                      "
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalFilterAutoschedulingCutOffTimeFrom = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                    </v-time-picker>
                  </v-dialog>
                </v-col>
                <v-col lg="2" sm="6" cols="12">
                  <v-dialog
                    ref="dialogFilterAutoschedulingCutOffTimeTo"
                    v-model="modalFilterAutoschedulingCutOffTimeTo"
                    :return-value.sync="filter.autoschedulingCutOffTimeToString"
                    persistent
                    width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filter.autoschedulingCutOffTimeToString"
                        :label="$t('clients.autoschedulingCutOffTimeTo')"
                        readonly
                        clearable
                        append-icon="far fa-clock"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker
                      v-if="modalFilterAutoschedulingCutOffTimeTo"
                      v-model="filter.autoschedulingCutOffTimeToString"
                      full-width
                      format="24hr"
                      :allowed-minutes="allowedStepTime"
                      @click:minute="
                        $refs.dialogFilterAutoschedulingCutOffTimeTo.save(
                          filter.autoschedulingCutOffTimeToString
                        )
                      "
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalFilterAutoschedulingCutOffTimeTo = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                    </v-time-picker>
                  </v-dialog>
                </v-col>
              </v-row>
            </v-container>
            <!-- clear filters -->
          </div>
          <!-- right side -->
          <div>
            <!-- Refresh table -->
            <crud-button
              large
              color="blue"
              @clicked="refreshItemsView()"
              icon="fa-sync-alt"
              :tooltip="$t('global.crud.buttons.refreshTable')"
            />
            <ExportExcel url="/clients" :filter="this.filter" />
            <ImportExcel
              url="/clients"
              @done="search"
              v-if="$checkPermission('CLIENTS')"
            />
          </div>
        </div>
      </div>
      <div id="dataContainer">
        <!-- Table -->
        <v-data-table
          :headers="headers"
          :items="items"
          item-key="code"
          :options.sync="options"
          :page.sync="pagination.page"
          :server-items-length="pagination.totalItems"
          :no-results-text="$t('global.datatable.noMatchingResults')"
          :no-data-text="$t('global.datatable.noDataAvailable')"
          :footer-props="{
            'items-per-page-options': rowsPerPageItems,
            'show-first-last-page': true,
            'show-current-page': true
          }"
          :loading="loading"
          light
          multi-sort
          dense
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.code">
                <td>
                  <!-- Edit record -->
                  <crud-button
                    v-if="$checkPermission('CLIENTS')"
                    x-small
                    color="orange"
                    icon="fa-pen"
                    :tooltip="$t('global.crud.buttons.edit')"
                    @clicked="edit(item.code)"
                  ></crud-button>

                  <!-- Delete record -->
                  <crud-button
                    v-if="
                      (item.deletionDate === undefined ||
                        item.deletionDate === null) &&
                        $checkPermission('CLIENTS')
                    "
                    x-small
                    color="red"
                    icon="fa-trash"
                    :tooltip="$t('global.crud.buttons.delete')"
                    @clicked="suspend(item.code)"
                  ></crud-button>

                  <!-- Active record -->
                  <crud-button
                    v-else-if="$checkPermission('CLIENTS')"
                    x-small
                    color="green"
                    icon="fa-redo"
                    :tooltip="$t('global.crud.buttons.restore')"
                    @clicked="restore(item.code)"
                  ></crud-button>

                  <!-- Time slots client -->
                  <crud-button
                    v-if="$checkPermission('TIMESLOTSCLIENTS')"
                    x-small
                    color="blue accent-3"
                    icon="fa-business-time"
                    :tooltip="$t('clients.timeSlots.button.title')"
                    @clicked="timeSlotsClients(item.code)"
                  ></crud-button>
                </td>
                <td>
                  {{ item.code }}
                </td>
                <td>
                  {{ item.name }}
                </td>
                <td><BooleanToIcon :value="item.autoscheduling" /></td>
                <td><BooleanToIcon :value="item.autoschedulingRespect" /></td>
                <td>
                  {{ minutesToHour(item.autoschedulingCutOffTime) }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
    </v-card>
    <v-dialog v-model="dialog" max-width="600" persistent no-click-animation>
      <validation-observer ref="obs" v-slot="{ invalid, validated, passes }">
        <v-card>
          <v-toolbar dark color="primary" dense flat>
            <v-toolbar-title class="white--text">
              {{ formTitle }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click.native="close()">
              <v-icon>fas fa-times</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="details-list">
            <v-form>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('clients.code')"
                    rules="required|max:30"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :label="$t('clients.code')"
                      v-model="item.code"
                      :disabled="editMode"
                      required
                      :error-messages="errors"
                      counter="30"
                    ></v-text-field>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('clients.name')"
                    rules="required|max:60"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :label="$t('clients.name')"
                      v-model="item.name"
                      required
                      :error-messages="errors"
                      counter="60"
                    ></v-text-field>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-row no-gutters>
                  <v-col class="d-inline-flex align-self-center">
                    {{ $t("clients.autoscheduling") }}
                  </v-col>
                  <v-col class="d-inline-flex justify-end">
                    <v-switch
                      v-model="item.autoscheduling"
                      class="ml-2"
                    ></v-switch>
                  </v-col>
                </v-row>
              </v-layout>
              <v-layout row wrap>
                <v-row no-gutters>
                  <v-col class="d-inline-flex align-self-center">
                    {{ $t("clients.autoschedulingRespect") }}
                  </v-col>
                  <v-col class="d-inline-flex justify-end">
                    <v-switch
                      v-model="item.autoschedulingRespect"
                      class="ml-2"
                    ></v-switch>
                  </v-col>
                </v-row>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <v-dialog
                    ref="dialogAutoschedulingCutOffTime"
                    v-model="modalAutoschedulingCutOffTime"
                    :return-value.sync="item.autoschedulingCutOffTimeString"
                    persistent
                    width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <validation-provider
                        :name="$t('clients.autoschedulingCutOffTime')"
                        v-slot="{ errors }"
                        vid="fieldAutoschedulingCutOffTime"
                      >
                        <v-text-field
                          v-model="item.autoschedulingCutOffTimeString"
                          :label="$t('clients.autoschedulingCutOffTime')"
                          readonly
                          clearable
                          append-icon="far fa-clock"
                          :error-messages="errors"
                          v-on="on"
                        ></v-text-field>
                      </validation-provider>
                    </template>
                    <v-time-picker
                      v-if="modalAutoschedulingCutOffTime"
                      v-model="item.autoschedulingCutOffTimeString"
                      full-width
                      format="24hr"
                      :allowed-minutes="allowedStepTime"
                      @click:minute="
                        $refs.dialogAutoschedulingCutOffTime.save(
                          item.autoschedulingCutOffTimeString
                        )
                      "
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalAutoschedulingCutOffTime = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                      <v-btn
                        text
                        color="primary"
                        @click="
                          $refs.dialogAutoschedulingCutOffTime.save(
                            item.autoschedulingCutOffTimeString
                          )
                        "
                      >
                        {{ $t("global.confirm") }}
                      </v-btn>
                    </v-time-picker>
                  </v-dialog>
                </v-flex>
              </v-layout>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="black" text @click.native="close()">
              {{ $t("global.buttons.close") }}
            </v-btn>
            <v-btn v-if="editMode" color="orange" text @click="passes(update)">
              {{ $t("global.buttons.update") }}
            </v-btn>
            <v-btn v-else color="green" text @click="passes(save)">
              {{ $t("global.buttons.create") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </validation-observer>
    </v-dialog>
  </div>
</template>

<script>
import CrudButton from "@/components/Button.vue";
import BooleanToIcon from "@/components/BooleanToIcon.vue";
import localhttp from "../js/https-commons";
import ExportExcel from "@/components/ExportExcel.vue";
import ImportExcel from "@/components/ImportExcel.vue";
import { minutesToHour, hourToMinutes } from "@/js/utils.js";

const CONTEXT = "/clients";
const ENTITY_NAME = "client.";

export default {
  components: {
    CrudButton,
    BooleanToIcon,
    ExportExcel,
    ImportExcel
  },
  name: "Client",
  data: () => ({
    modalAutoschedulingCutOffTime: false,
    modalFilterAutoschedulingCutOffTimeFrom: false,
    modalFilterAutoschedulingCutOffTimeTo: false,
    loading: false,
    searchTableWaiting: false,
    editMode: false,
    dialog: false,
    item: {},
    items: [],
    filter: {
      allFields: "",
      deletedItems: "ALL",
      autoscheduling: "",
      autoschedulingCutOffTimeFrom: "",
      autoschedulingCutOffTimeFromString: "",
      autoschedulingCutOffTimeTo: "",
      autoschedulingCutOffTimeToString: ""
    },
    options: {},
    pagination: {
      totalItems: 0,
      page: 1
    },
    rowsPerPageItems: [10, 20, 30, 40]
  }),
  computed: {
    deletedItemsValues: function() {
      return [
        { text: this.$t("global.status.all"), value: "ALL" },
        { text: this.$t("global.status.active"), value: "ACTIVE" },
        { text: this.$t("global.status.deleted"), value: "DELETED" }
      ];
    },
    autoschedulingValues: function() {
      return [
        { text: this.$t("global.all"), value: "" },
        { text: this.$t("global.yes"), value: "S" },
        { text: this.$t("global.no"), value: "N" }
      ];
    },
    formTitle() {
      return this.editMode
        ? this.$t("clients.title.edit")
        : this.$t("clients.title.new");
    },
    headers() {
      return [
        {
          text: this.$t("global.datatable.header.actions"),
          value: "actions",
          sortable: false
        },
        {
          text: this.$t("clients.datatable.code"),
          value: ENTITY_NAME + "code"
        },
        {
          text: this.$t("clients.datatable.name"),
          value: ENTITY_NAME + "name"
        },
        {
          text: this.$t("clients.datatable.autoscheduling"),
          value: ENTITY_NAME + "autoscheduling"
        },
        {
          text: this.$t("clients.datatable.autoschedulingRespect"),
          value: ENTITY_NAME + "autoschedulingRespect"
        },
        {
          text: this.$t("clients.datatable.autoschedulingCutOffTime"),
          value: ENTITY_NAME + "autoschedulingCutOffTime"
        }
      ];
    }
  },
  watch: {
    options: {
      handler() {
        this.search();
      },
      deep: true
    },
    "pagination.totalItems": {
      handler() {
        this.pagination.page = 1;
      },
      deep: true
    },
    "filter.allFields": function() {
      if (!this.searchTableWaiting) {
        setTimeout(() => {
          this.search();
          this.searchTableWaiting = false;
        }, 500);
      }
      this.searchTableWaiting = true;
    }
  },
  methods: {
    search() {
      console.debug("Search elements");

      // Items have already been requested
      if (this.loading) return;

      this.loading = true;

      this.filter.autoschedulingCutOffTimeFrom =
        this.filter.autoschedulingCutOffTimeFromString != null
          ? hourToMinutes(this.filter.autoschedulingCutOffTimeFromString)
          : null;
      this.filter.autoschedulingCutOffTimeTo =
        this.filter.autoschedulingCutOffTimeToString != null
          ? hourToMinutes(this.filter.autoschedulingCutOffTimeToString)
          : null;

      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      this.filter.sortBy = sortBy;
      this.filter.sortDesc = sortDesc;
      this.filter.page = page;
      this.filter.itemsPerPage = itemsPerPage;

      localhttp
        .post(CONTEXT + "/allPagination", this.filter)
        .then(response => {
          this.pagination.totalItems = response.data.totalElements;
          this.items = response.data.content;
          this.loading = false;
        })
        .catch(e => {
          console.log(e);
          this.loading = false;
          this.$showError();
        });
    },
    create() {
      console.debug("Create element");
      this.editMode = false;
      this.resetDialog();
      this.dialog = true;
    },
    async save() {
      console.debug("Save form");

      this.item.autoschedulingCutOffTime = hourToMinutes(
        this.item.autoschedulingCutOffTimeString
      );

      localhttp
        .post(CONTEXT + "/create", this.item)
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    edit(id) {
      console.debug("Edit element " + id);
      this.editMode = true;
      this.resetDialog();
      localhttp
        .get(CONTEXT + "/" + id)
        .then(response => {
          this.item = response.data;
          this.editPrepare(this.item);
          this.dialog = true;
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    editPrepare(item) {
      item.autoschedulingCutOffTimeString = minutesToHour(
        item.autoschedulingCutOffTime
      );
    },
    async update() {
      console.debug("Update form");
      this.item.autoschedulingCutOffTime =
        this.item.autoschedulingCutOffTimeString != null
          ? hourToMinutes(this.item.autoschedulingCutOffTimeString)
          : null;

      localhttp
        .put(CONTEXT + "/update/" + this.item.code, this.item)
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    async suspend(id) {
      console.debug("Suspend element " + id);
      if (
        await this.$root.$confirm(
          this.$t("global.confirm"),
          this.$t("global.action.suspend.confirm"),
          { color: "blue" }
        )
      ) {
        localhttp
          .put(CONTEXT + "/delete/" + id)
          .then(response => {
            console.log(response.data);
            this.search();
          })
          .catch(e => {
            console.log(e);
            this.$showErrorCode(e.response.status);
          });
      }
    },
    restore(id) {
      console.debug("Restore element " + id);
      localhttp
        .put(CONTEXT + "/activate/" + id)
        .then(response => {
          console.log(response.data);
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    refreshItemsView() {
      console.debug("Refresh table");
      this.search();
    },
    close() {
      this.dialog = false;
    },
    resetDialog() {
      this.item = {};
      requestAnimationFrame(() => {
        if (this.$refs.obs !== undefined) {
          this.$refs.obs.reset();
        }
      });
    },
    timeSlotsClients(code) {
      console.log(code);
      this.$router.push({
        name: "timeSlotsClients",
        query: {
          client: code
        }
      });
    },
    allowedStepTime: m => m % 5 === 0,
    minutesToHour,
    hourToMinutes
  }
};
</script>
